import { Row, Col, Skeleton, Flex } from 'antd';
import { useGetDailyGoalsStatistics } from 'api/statistic/use-get-daily-load-goals-statistics';
import { useGetGoalsByManagerStatistic } from 'api/statistic/use-get-goals-by-manager-statistic';
import { useGetGoalsStatistic } from 'api/statistic/use-get-goals-statistic';
import { useGetPaymentPeriodStatistics } from 'api/statistic/use-get-payment-period-statistics';
import { useGetProfitAggregatedStatistic } from 'api/statistic/use-get-profit-aggregated-statistic';
import { useAuthStore } from 'shared/stores';
import { useGoalsInsightsFilter } from 'shared/stores/use-goals-insights-filters';
import type { IStatisticProfitFilter } from 'shared/stores/use-statistic-profit-filters';
import { formatDate } from 'shared/utils/time/format-date';
import { DailyLdmRings } from 'components/insights/daily-ldm-rings';
import { ManagerPerformanceInsights } from 'components/insights/manager-performance-insights';
import { RingsInsights } from 'components/insights/rings-insights';
import { GoalsInsightsHeader } from 'components/statistics/goals-insights-header';
import { PaymentPeriodStatistic } from 'components/statistics/payment-period-statistic';
import { ProfitAggregatedStatistic } from 'components/statistics/profit-aggregated-statistic/profit-aggregated-statistic';

export const CustomerInsights = () => {
  const { filters } = useGoalsInsightsFilter();

  const { transportLine, cargoTypes = [] } = filters;
  const date = formatDate(filters.date);
  const { user } = useAuthStore();

  const { data: dailyGoals, isLoading: isDailyGoalsLoading } = useGetGoalsStatistic({
    date,
    period: 'day',
    transportLine,
    cargoTypes,
  });
  const { data: weeklyGoals, isLoading: isWeeklyGoalsLoading } = useGetGoalsStatistic({
    date,
    period: 'week',
    transportLine,
    cargoTypes,
  });
  const { data: dailyGoalsByManager, isLoading: isDailyGoalsByManagerLoading } = useGetGoalsByManagerStatistic({
    date,
    period: 'day',
    transportLine,
    cargoTypes,
  });
  const { data: weeklyGoalsByManager, isLoading: isWeeklyGoalsByManagerLoading } = useGetGoalsByManagerStatistic({
    date,
    period: 'week',
    transportLine,
    cargoTypes,
  });

  const profitFilter: IStatisticProfitFilter = {
    userId: user?.id,
    date: [filters.date.startOf('month'), filters.date.endOf('month')],
  };

  const paymentPeriodFilter: IStatisticProfitFilter = {
    userId: user?.id,
    date: [filters.date.subtract(6, 'months'), filters.date],
  };

  const { data: aggregatedStatisticData, isLoading: isAggregatedStatisticLoading } = useGetProfitAggregatedStatistic({
    filters: profitFilter,
  });

  const { data: paymentPeriodData, isLoading: isPaymentPeriodLoading } = useGetPaymentPeriodStatistics({
    filters: paymentPeriodFilter,
  });

  const { data: dailyLoadGoalsStatisticsData, isLoading: isDailyLoadGoalsStatisticsLoading } =
    useGetDailyGoalsStatistics({
      date,
      transportLine,
      cargoTypes,
    });

  const isLoading =
    isDailyGoalsLoading ||
    isWeeklyGoalsLoading ||
    isDailyGoalsByManagerLoading ||
    isWeeklyGoalsByManagerLoading ||
    isAggregatedStatisticLoading ||
    isDailyLoadGoalsStatisticsLoading ||
    isPaymentPeriodLoading;

  const dailyMoneyGoalsByManager =
    dailyGoalsByManager?.money.map<[string, number]>((item) => [item.user, item.value]) || [];
  const dailyLdmGoalsByManager =
    dailyGoalsByManager?.ldm.map<[string, number]>((item) => [item.user, item.value]) || [];
  const weeklyMoneyGoalsByManager =
    weeklyGoalsByManager?.money.map<[string, number]>((item) => [item.user, item.value]) || [];
  const weeklyLdmGoalsByManager =
    weeklyGoalsByManager?.ldm.map<[string, number]>((item) => [item.user, item.value]) || [];

  return (
    <>
      <GoalsInsightsHeader />
      <Skeleton loading={isLoading}>
        <Flex gap={16} style={{ marginBlock: '16px' }}>
          {aggregatedStatisticData ? (
            <>
              <ProfitAggregatedStatistic value={aggregatedStatisticData.value} isPersonal />

              {paymentPeriodData?.value && (
                <PaymentPeriodStatistic
                  title='Payment period'
                  value={paymentPeriodData.value}
                  showAmount={false}
                  showAvgDaysCreatedToStart={false}
                />
              )}
            </>
          ) : null}
        </Flex>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            {dailyGoals && (
              <RingsInsights
                title='Daily goals'
                moneyValue={dailyGoals.money.value}
                moneyGoal={dailyGoals.money.goal}
                moneyPercent={dailyGoals.money.percent}
                ldmValue={dailyGoals.ldm.value}
                ldmGoal={dailyGoals.ldm.goal}
                ldmPercent={dailyGoals.ldm.percent}
              />
            )}
          </Col>
          <Col span={12}>
            {weeklyGoals && (
              <RingsInsights
                title='Weekly goals'
                moneyValue={weeklyGoals.money.value}
                moneyGoal={weeklyGoals.money.goal}
                moneyPercent={weeklyGoals.money.percent}
                ldmValue={weeklyGoals.ldm.value}
                ldmGoal={weeklyGoals.ldm.goal}
                ldmPercent={weeklyGoals.ldm.percent}
              />
            )}
          </Col>

          <Col span={24}>
            <DailyLdmRings
              data={dailyLoadGoalsStatisticsData?.dailyData || []}
              ldmGoal={dailyLoadGoalsStatisticsData?.ldmGoal || 0}
              weightGoal={dailyLoadGoalsStatisticsData?.weightGoal || 0}
            />
          </Col>

          <Col span={12}>
            <ManagerPerformanceInsights
              title='Daily amount of money by manager'
              seriesData={dailyMoneyGoalsByManager}
            />
          </Col>
          <Col span={12}>
            <ManagerPerformanceInsights
              title='Weekly amount of money by manager'
              seriesData={weeklyMoneyGoalsByManager}
            />
          </Col>
          <Col span={12}>
            <ManagerPerformanceInsights title='Daily amount of LDM by manager' seriesData={dailyLdmGoalsByManager} />
          </Col>
          <Col span={12}>
            <ManagerPerformanceInsights title='Weekly amount of LDM by manager' seriesData={weeklyLdmGoalsByManager} />
          </Col>
        </Row>
      </Skeleton>
    </>
  );
};
